import React, { useState } from 'react';
import { ICONS } from 'Components/utils/icon/icons';
import { useResponsive } from 'Components/utils/responsive/ResponsiveContext';
import { t } from 'Src/i18n';
import { TRACK_EVENT_NAMES } from 'Tracking/constants';
import { useTrackEvent } from 'Tracking/tracking-api/useTrackEvent';
import { getNbAccommodations } from 'Utils/accommodation/accommodation.utils';
import { Title } from 'Components/frameworks/title/Title';
import { AccommodationOption } from 'Components/widgets/accommodation-option/AccommodationOption';
import { AccommodationOptionCurrent } from 'Components/widgets/accommodation-option-current/AccommodationOptionCurrent';
import { BookingAccommodations } from 'Components/widgets/accommodations/BookingAccommodations';
import {
  DoubleColLayer,
  EndCol,
  StartCol,
  START_COL_CMP,
  END_COL_CMP,
} from 'Components/widgets/double-col-layer/DoubleColLayer';
import { Paginate } from 'Components/widgets/paginate/Paginate';
type Props = {
  accommodationsSets: Array<AccommodationsSet>;
  currency: string;
  filteredAccommodationSets: Array<AccommodationsSet>;
  onAddAccommodation: (arg0: string | null | undefined) => void;
  proposal: Proposal;
  product: Product;
  setSelectedRate: (arg0: string) => void;
  selectedRate: string;
  streetViews?: Array<StreetViewWithAccommodationCategoryId>;
};
export const AdditionalAccommodationsList = ({
  accommodationsSets,
  currency,
  filteredAccommodationSets,
  onAddAccommodation,
  proposal,
  product,
  selectedRate,
  setSelectedRate,
  streetViews,
}: Props) => {
  const { isTablet } = useResponsive();
  const { trackClickEvent } = useTrackEvent();
  const iterationNumber = 5;
  const [forceInitialShowingItemsNumber, setForceInitialShowingItemsNumber] =
    useState(iterationNumber);

  if (accommodationsSets.length < 1) {
    return null;
  }

  const headerHeight = 60 + 70;
  const titleHeight = 27.5;
  const paddingHeight = 40;
  return (
    <DoubleColLayer
      leftButtonLabel={t('booking.accommodation.moreRooms.roomRecommendation')}
      rightButtonLabel={`${t('booking.accommodation.moreRooms.otherProposals')} (${filteredAccommodationSets.length}
    )`}
      initialActiveCol={isTablet ? START_COL_CMP : END_COL_CMP}
      stickyTopPosition="140px"
    >
      {/* @ts-expect-error TODO(flow-migration) : activeCol & isTablet props are added by parent component */}
      <StartCol headerOffset={8.75}>
        <Title
          className="mb-20 hidden sm:flex"
          hLevel={3}
          iconName={ICONS.CHECK}
          theme="title-brand-n3"
        >
          {t('booking.accommodation.moreRooms.roomRecommendation')}
        </Title>
        <AccommodationOptionCurrent
          onSelect={() => {
            trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION_LAYER, {
              detail_click: 'current_room_validate',
            });
            onAddAccommodation();
          }}
          style={
            isTablet
              ? {
                  maxHeight: `calc(100vh - ${titleHeight}px - ${headerHeight}px - ${paddingHeight}px)`,
                }
              : null
          }
        >
          <BookingAccommodations
            accommodations={proposal.accommodations}
            isInMoreRoomLayer
            product={product}
            proposal={proposal}
            selectedRate={selectedRate}
            setSelectedRate={setSelectedRate}
            streetViews={streetViews}
            theme="medium"
          />
        </AccommodationOptionCurrent>
      </StartCol>
      {/* @ts-expect-error TODO(flow-migration) : activeCol & isTablet props are added by parent component */}
      <EndCol dataTestId="AdditionalAccommodationsList">
        <Title className="mb-20 hidden sm:flex" hLevel={3} theme="title-brand-n3">
          {filteredAccommodationSets.length}
          &nbsp;
          {t('booking.accommodation.moreRooms.otherProposals')}
        </Title>
        <Paginate
          forceInitialShowingItemsNumber={!isTablet ? forceInitialShowingItemsNumber : 0}
          iterationNumber={iterationNumber}
          seeMoreLabel={t('common.viewMore')}
          setForceInitialShowingItemsNumber={setForceInitialShowingItemsNumber}
        >
          {filteredAccommodationSets.map((accommodationsSet, idx) => (
            <AccommodationOption
              className="mb-40"
              id={accommodationsSet.id}
              index={idx + 1}
              key={accommodationsSet.id}
              onSelect={() => {
                trackClickEvent(TRACK_EVENT_NAMES.CLICK_ACCOMMODATION_LAYER, {
                  detail_click: 'edit_room_validate',
                });
                onAddAccommodation(accommodationsSet.id);
              }}
              price={{
                currency,
                nigthly: accommodationsSet.differentialPriceByNight?.value || 0,
                total: accommodationsSet.totalDifferentialPrice?.value || 0,
                isUpgradableOnline: accommodationsSet.isUpgradableOnline,
              }}
            >
              <BookingAccommodations
                accommodations={accommodationsSet.accommodations}
                accommodationsCount={getNbAccommodations(accommodationsSet)}
                isInMoreRoomLayer
                product={product}
                proposal={proposal}
                remainingStock={accommodationsSet.remainingStock}
                selectedRate={selectedRate}
                setSelectedRate={setSelectedRate}
                streetViews={streetViews}
                theme="medium"
              />
            </AccommodationOption>
          ))}
        </Paginate>
      </EndCol>
    </DoubleColLayer>
  );
};
